/**
 * Draw an itinerary PDF (US Letter) and share or download it.
 */

import { jsPDF } from 'jspdf';
import { tripDetailEn } from '../content/trips.en';
import {
  PDF_PAGE,
  packDayTiles,
  type PackedTilePage,
} from '../utils/itineraryPdfLayout';
import type { ItineraryPdfModel, PdfTile } from '../utils/itineraryPdfModel';

function drawLines(
  doc: jsPDF,
  lines: string[],
  x: number,
  y: number
): void {
  let cursor = y;
  lines.forEach((line, index) => {
    doc.setFont('helvetica', index === 0 ? 'bold' : 'normal');
    doc.setFontSize(index === 0 ? 12 : 11);
    doc.text(line, x, cursor);
    cursor += PDF_PAGE.lineHeight;
  });
}

function drawTile(doc: jsPDF, tile: PdfTile, x: number, y: number, width: number): number {
  doc.setDrawColor(220);
  doc.setFillColor(245, 245, 245);
  doc.setTextColor(20);
  doc.roundedRect(x, y, width, tile.height, 6, 6, 'FD');
  drawLines(doc, tile.lines, x + 10, y + 16);
  return tile.height;
}

function drawCover(doc: jsPDF, model: ItineraryPdfModel): void {
  const x = PDF_PAGE.margin;
  const width = PDF_PAGE.width - PDF_PAGE.margin * 2;
  let y = PDF_PAGE.margin + 8;

  const nextLine = (needed: number): void => {
    if (y + needed <= PDF_PAGE.height - PDF_PAGE.margin) return;
    doc.addPage();
    y = PDF_PAGE.margin + 16;
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(0);
  const titleLines = doc.splitTextToSize(model.title, width) as string[];
  nextLine(titleLines.length * 28);
  doc.text(titleLines, x, y);
  y += titleLines.length * 28 + 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(90);
  nextLine(22);
  doc.text(model.cover.dateRange, x, y);
  y += 36;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(0);
  nextLine(24);
  doc.text(tripDetailEn.pdfSummaryTitle, x, y);
  y += 26;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  nextLine(20);
  doc.text(model.cover.summary, x, y);
  y += 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  nextLine(24);
  doc.text(tripDetailEn.pdfCitiesVisited, x, y);
  y += 24;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(14);
  const cities = model.cover.cities.length > 0 ? model.cover.cities : ['—'];
  for (const city of cities) {
    const cityLines = doc.splitTextToSize(`• ${city}`, width) as string[];
    nextLine(cityLines.length * 18);
    doc.text(cityLines, x, y);
    y += cityLines.length * 18 + 4;
  }
}

function drawContinuation(doc: jsPDF, title: string): number {
  const y = PDF_PAGE.margin + 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0);
  const maxTitleWidth = PDF_PAGE.width - PDF_PAGE.margin * 2 - 90;
  const titleLine = (doc.splitTextToSize(title, maxTitleWidth) as string[])[0] ?? title;
  doc.text(titleLine, PDF_PAGE.margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text(tripDetailEn.pdfContinued, PDF_PAGE.width - PDF_PAGE.margin, y, {
    align: 'right',
  });
  return PDF_PAGE.margin + PDF_PAGE.continuationHeaderHeight;
}

function drawDayPage(doc: jsPDF, page: PackedTilePage<PdfTile>): void {
  doc.addPage();
  let y: number = PDF_PAGE.margin;
  if (page.continuationTitle) {
    y = drawContinuation(doc, page.continuationTitle);
  }
  const x = PDF_PAGE.margin;
  const width = PDF_PAGE.width - PDF_PAGE.margin * 2;
  page.tiles.forEach((entry, index) => {
    if (index > 0) y += PDF_PAGE.tileSpacing;
    drawTile(doc, entry, x, y, width);
    y += entry.height;
  });
}

export function renderItineraryPdf(model: ItineraryPdfModel): Blob {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  drawCover(doc, model);
  const pages = packDayTiles(model.days);
  for (const page of pages) {
    drawDayPage(doc, page);
  }
  const data = doc.output('arraybuffer');
  return new Blob([data], { type: 'application/pdf' });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Share the PDF when the browser allows file shares; otherwise download it. */
export async function shareOrDownloadItineraryPdf(
  blob: Blob,
  fileName: string,
  title: string
): Promise<void> {
  const file = new File([blob], fileName, { type: 'application/pdf' });
  const nav = typeof navigator === 'undefined' ? null : navigator;
  if (nav && typeof nav.canShare === 'function' && typeof nav.share === 'function') {
    let canShareFiles = false;
    try {
      canShareFiles = nav.canShare({ files: [file] });
    } catch {
      canShareFiles = false;
    }
    if (canShareFiles) {
      await nav.share({ files: [file], title });
      return;
    }
  }
  downloadBlob(blob, fileName);
}
